import { HostAppName, HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";
import { LogError } from '../../utils/ErrorTelemetry';
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { ErrorStringFromException } from "../../utils/utils";
import { HostAppEvent } from "../manager/eventManager/HostAppEventReceiver";
import { HostAppEventHandlerFactory } from "../factory/HostAppEventHandlerFactory";
import { OneNoteUserEventsKeys } from "../../frontend/common/telemetry/OneNoteEventsKeys";
import { LogEvents } from "../../utils/LogEvents";
import { EventCategory, EventDirection } from "../TelemetryLogger/LogEvent";
import { IEventListener, PlaybackPayload, TranscriptionNotifyEvent } from "../interfaces/IEventListener";
import { BroadcastService } from "../manager/eventManager/BroadcastService";
import { HostAppUtil } from "../office-js/HostAppUtil";

type OneNotePlaybackPayload = {
  pageId: string,
  playbackPayload: PlaybackPayload
};

export class OneNoteEventNotifier implements IEventListener {

  constructor() {
    BroadcastService.GetInstance().RegisterEventListenerForEvents(this, [
      TranscriptionNotifyEvent.AUDIO_PAUSE,
      TranscriptionNotifyEvent.AUDIO_PLAY,
      TranscriptionNotifyEvent.TRANSCRIPION_LAUNCHED,
      TranscriptionNotifyEvent.TRANSCRIPTION_PLAYBACK_MODE_CLOSED,
      TranscriptionNotifyEvent.TRANSCRIPTION_PLAYBACK_MODE_ON,
      TranscriptionNotifyEvent.TRANSCRIPTION_RECORDING_STARTED,
      TranscriptionNotifyEvent.TRANSCRIPTION_RECORDING_STOPPED]);
    HostAppEventHandlerFactory.GetInstance().on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_DELETED], this.onPageDeleted);
  }

  private onPageDeleted = (pageId: string) => {
    if (pageId == HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId)) {
      BroadcastService.GetInstance().UnregisterEventListener(this);
    }
    HostAppEventHandlerFactory.GetInstance().off(HostAppEvent[HostAppEvent.ONENOTE_PAGE_DELETED], this.onPageDeleted);
  }

  onReceive(event: TranscriptionNotifyEvent, payload?: any) {
    // Defensive: only proceed when actually running inside OneNote host.
    // Prevents leaked listeners (e.g. in jest --runInBand) from kicking off
    // OneNote.run() promises whose pending I/O can keep Jest from exiting.
    if (HostAppUtil.GetHostAppName() !== HostAppName.ONENOTE) {
      return;
    }
    switch (event) {
      case TranscriptionNotifyEvent.AUDIO_PLAY:
      case TranscriptionNotifyEvent.AUDIO_PAUSE:
      case TranscriptionNotifyEvent.TRANSCRIPTION_PLAYBACK_MODE_ON:
        const oneNotePayload: OneNotePlaybackPayload = {
          pageId: HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId),
          playbackPayload: payload
        };
        const oneNotePayloadString = JSON.stringify(oneNotePayload);
        LogEvents.OneNoteUserEvents.FlushEventAdhoc(
          [{ key: OneNoteUserEventsKeys.TranscriptionSessionId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId) },
          { key: OneNoteUserEventsKeys.EventCategory, value: EventCategory.Audio },
          { key: OneNoteUserEventsKeys.EventType, value: TranscriptionNotifyEvent[event] },
          { key: OneNoteUserEventsKeys.EventDirection, value: EventDirection.Sent },
          { key: OneNoteUserEventsKeys.EventInfo, value: oneNotePayloadString }]);
        //ToDo: Will log Transcription State as well

        this.SendTranscriptionEvent(event, oneNotePayloadString);
        return;
      case TranscriptionNotifyEvent.TRANSCRIPTION_PLAYBACK_MODE_CLOSED:
      case TranscriptionNotifyEvent.TRANSCRIPION_LAUNCHED:
      case TranscriptionNotifyEvent.TRANSCRIPTION_RECORDING_STARTED:
      case TranscriptionNotifyEvent.TRANSCRIPTION_RECORDING_STOPPED:
        LogEvents.OneNoteUserEvents.FlushEventAdhoc(
          [{ key: OneNoteUserEventsKeys.TranscriptionSessionId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId) },
          { key: OneNoteUserEventsKeys.EventCategory, value: EventCategory.Audio },
          { key: OneNoteUserEventsKeys.EventType, value: TranscriptionNotifyEvent[event] },
          { key: OneNoteUserEventsKeys.EventDirection, value: EventDirection.Sent },
          { key: OneNoteUserEventsKeys.OneNotePageId, value: HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId) }]);
        //ToDo: Will log Transcription State as well
        this.SendTranscriptionEvent(event, JSON.stringify({ pageId: HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId) }));
        return;
    }
  }

  /**
  * SendTranscriptionEvent is triggered from Transcription side to HostApp.
  * @method SendTranscriptionEvent
  * @param {eventId} number representing event, check @enum {TranscriptionEvent} for more details.
  * @param {eventInfo} string Json String carrying payload details related to event.
  * @return {} Promise<void>
  */
  private async SendTranscriptionEvent(eventId: number, eventInfo?: string): Promise<void> {
    try {
      await OneNote.run(async (context) => {
        const activePage = context.application.getActivePage();
        const transcription = (<any>activePage).transcription;
        transcription.notify(eventId, eventInfo);
        await context.sync();
      });
    } catch (e) {
      LogError(LogEventNames.Warning, "Sending notify event for eventID - " + eventId + " in OneNoteEventListener failed with error- " + ErrorStringFromException(e));
    }
  }
}